import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/lib/auth";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Templates from "@/pages/Templates";
import TemplateBuilder from "@/pages/TemplateBuilder";
import Assignments from "@/pages/Assignments";
import Responses from "@/pages/Responses";
import ResponseDetail from "@/pages/ResponseDetail";
import PatientResponse from "@/pages/PatientResponse";
import Kiosk from "@/pages/Kiosk";
import InviteAccept from "@/pages/InviteAccept";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Sales from "@/pages/Sales";
import LegalNotice from "@/pages/LegalNotice";
import CabinetJoin from "@/pages/CabinetJoin";
import PatientRecords from "@/pages/PatientRecords";
import PatientRecordDetail from "@/pages/PatientRecordDetail";
import Studies from "@/pages/Studies";
import Settings from "@/pages/Settings";
import PaymentSuccess from "@/pages/PaymentSuccess";
// Backoffice superadmin
import { SuperadminProtected } from "@/pages/superadmin/SuperadminLayout";
import SuperadminDashboard from "@/pages/superadmin/SuperadminDashboard";
import SuperadminUsers from "@/pages/superadmin/SuperadminUsers";
import SuperadminOrganizations from "@/pages/superadmin/SuperadminOrganizations";
import SuperadminStripe from "@/pages/superadmin/SuperadminStripe";
import SuperadminEmails from "@/pages/superadmin/SuperadminEmails";
import SuperadminAudit from "@/pages/superadmin/SuperadminAudit";
import SuperadminFlags from "@/pages/superadmin/SuperadminFlags";
import SuperadminMaintenance from "@/pages/superadmin/SuperadminMaintenance";
import SuperadminModeration from "@/pages/superadmin/SuperadminModeration";
import SuperadminMetrics from "@/pages/superadmin/SuperadminMetrics";
import SuperadminLlm from "@/pages/superadmin/SuperadminLlm";
import SuperadminSecurity from "@/pages/superadmin/SuperadminSecurity";

function AppRouter() {
  const location = useLocation();
  // Reset scroll to top on route change (unless a hash anchor is targeted).
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  // OAuth callback detection (session_id in URL fragment)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/templates" element={<Templates />} />
      <Route path="/templates/new" element={<TemplateBuilder />} />
      <Route path="/templates/:id/edit" element={<TemplateBuilder />} />
      <Route path="/library" element={<Navigate to="/templates" replace />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/cabinet" element={<Navigate to="/settings?tab=cabinet" replace />} />
      <Route path="/audit" element={<Navigate to="/settings?tab=audit" replace />} />
      <Route path="/patients" element={<Navigate to="/patient-records" replace />} />
      <Route path="/patient-records" element={<PatientRecords />} />
      <Route path="/patient-records/:patientId" element={<PatientRecordDetail />} />
      <Route path="/assignments" element={<Assignments />} />
      <Route path="/responses" element={<Responses />} />
      <Route path="/responses/:id" element={<ResponseDetail />} />
      <Route path="/studies" element={<Studies />} />
      <Route path="/cabinet/join" element={<CabinetJoin />} />
      <Route path="/confidentialite" element={<Privacy />} />
      <Route path="/cgu" element={<Terms />} />
      <Route path="/cgv" element={<Sales />} />
      <Route path="/mentions-legales" element={<LegalNotice />} />
      <Route path="/q/:token" element={<PatientResponse />} />
      <Route path="/kiosk/:publicToken" element={<Kiosk />} />
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />

      {/* Backoffice superadmin — /superadmin/* */}
      <Route path="/superadmin" element={<SuperadminProtected><SuperadminDashboard /></SuperadminProtected>} />
      <Route path="/superadmin/users" element={<SuperadminProtected><SuperadminUsers /></SuperadminProtected>} />
      <Route path="/superadmin/organizations" element={<SuperadminProtected><SuperadminOrganizations /></SuperadminProtected>} />
      <Route path="/superadmin/stripe" element={<SuperadminProtected><SuperadminStripe /></SuperadminProtected>} />
      <Route path="/superadmin/emails" element={<SuperadminProtected><SuperadminEmails /></SuperadminProtected>} />
      <Route path="/superadmin/audit" element={<SuperadminProtected><SuperadminAudit /></SuperadminProtected>} />
      <Route path="/superadmin/flags" element={<SuperadminProtected><SuperadminFlags /></SuperadminProtected>} />
      <Route path="/superadmin/maintenance" element={<SuperadminProtected><SuperadminMaintenance /></SuperadminProtected>} />
      <Route path="/superadmin/moderation" element={<SuperadminProtected><SuperadminModeration /></SuperadminProtected>} />
      <Route path="/superadmin/metrics" element={<SuperadminProtected><SuperadminMetrics /></SuperadminProtected>} />
      <Route path="/superadmin/llm" element={<SuperadminProtected><SuperadminLlm /></SuperadminProtected>} />
      <Route path="/superadmin/security" element={<SuperadminProtected><SuperadminSecurity /></SuperadminProtected>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
